import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
// v2
import PropertyManagersPanel from '../../components/shared/PropertyManagersPanel';
import api from '../../utils/api';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_CONFIG = {
  active:   { label: 'Active',   bg: '#dcfce7', color: '#16a34a' },
  draft:    { label: 'Draft',    bg: '#f1f5f9', color: '#64748b' },
  inactive: { label: 'Inactive', bg: '#fee2e2', color: '#dc2626' },
};

export default function LandlordPortfolio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/employee/landlords/' + id)
      .then(res => setData(res.data))
      .catch(() => setError('Could not load landlord portfolio'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading portfolio...</div>
    </div>
  );

  const { landlord, properties, totals } = data || {};

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem', maxWidth: 1100 }}>

        {/* Header */}
        <button onClick={() => navigate('/employee/landlords')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>
          ← Back to Landlords
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
              {landlord?.first_name} {landlord?.last_name}
            </h1>
            {landlord?.company_name && <div style={{ fontSize: 15, color: '#64748b', marginBottom: 2 }}>{landlord.company_name}</div>}
            <div style={{ fontSize: 13, color: '#94a3b8' }}>{landlord?.email} {landlord?.phone ? '· ' + landlord.phone : ''}</div>
            {landlord?.account_manager_name && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
                Account Manager: <strong>{landlord.account_manager_name}</strong> · {landlord.account_manager_email}
              </div>
            )}
          </div>
          <span style={{
            background: landlord?.status === 'active' ? '#dcfce7' : '#fee2e2',
            color: landlord?.status === 'active' ? '#16a34a' : '#dc2626',
            borderRadius: 100, padding: '6px 18px', fontSize: 13, fontWeight: 700,
          }}>
            {landlord?.status}
          </span>
        </div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#dc2626', marginBottom: 16 }}>{error}</div>}

        {/* Portfolio totals */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Properties', value: totals?.total_properties || 0 },
            { label: 'Total Units', value: totals?.total_units || 0 },
            { label: 'Active Tenants', value: totals?.active_tenants || 0, color: '#16a34a' },
            { label: 'Revenue MTD', value: '$' + fmt(totals?.revenue_mtd), color: '#2563eb' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '1.2rem 1.5rem', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color || '#1e293b' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Properties list */}
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Properties ({properties?.length || 0})</div>
          </div>

          {(!properties || properties.length === 0) && (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>TESTMARKER_XYZ No properties yet.</div>
          )}

          {properties?.map((p, i) => {
            const statusCfg = STATUS_CONFIG[p.status] || STATUS_CONFIG['draft'];
            const adoptionPct = p.unit_count > 0 ? Math.round((p.active_tenants / p.unit_count) * 100) : 0;
            const contractExpiry = p.contract_end_date ? new Date(p.contract_end_date) : null;
            const daysToExpiry = contractExpiry ? Math.ceil((contractExpiry - new Date()) / (1000 * 60 * 60 * 24)) : null;
            const expiryWarn = daysToExpiry !== null && daysToExpiry < 90;

            return (
              <div key={p.id}
                onClick={() => navigate('/employee/properties/' + p.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 20px', borderBottom: i < properties.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s', flexWrap: 'wrap', gap: 12,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f7f9ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{p.name}</div>
                    <span style={{ background: statusCfg.bg, color: statusCfg.color, borderRadius: 100, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                      {statusCfg.label}
                    </span>
                    {expiryWarn && (
                      <span style={{ background: '#fffbeb', color: '#92400e', borderRadius: 100, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                        ⚠️ Expires {daysToExpiry}d
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.address}, {p.city}, {p.state} {p.zip}</div>
                  {p.launch_date && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>🗓 Launch: {fmtDate(p.launch_date)}</div>}
                </div>

                <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{p.unit_count || 0}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Units</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>{p.active_tenants || 0}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Active</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: adoptionPct >= 70 ? '#16a34a' : '#f59e0b' }}>{adoptionPct}%</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Adoption</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{p.isp || '—'}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>ISP</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#2563eb' }}>${fmt(p.revenue_mtd)}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Rev MTD</div>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: 20 }}>›</span>
                </div>
              </div>
            );
          })}
        </div>

        <PropertyManagersPanel landlordId={id} properties={data?.properties || []} isEmployee={true} />

        <LegalFooter />
      </div>
    </div>
  );
}
